import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Typography, Select, Tabs, Empty, Space, Button, DatePicker } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import dayjs from 'dayjs';
import api from '../api';

const { RangePicker } = DatePicker;

import OverviewTab    from '../components/campaignSummary/OverviewTab';
import AgentGoalsTab  from '../components/campaignSummary/AgentGoalsTab';
import EvaluationsTab from '../components/campaignSummary/EvaluationsTab';
import CallStatsTab   from '../components/campaignSummary/CallStatsTab';
import AgentsTab      from '../components/campaignSummary/AgentsTab';
import ErrorsTab      from '../components/campaignSummary/ErrorsTab';
import CoachingTab    from '../components/campaignSummary/CoachingTab';
import ComplianceTab  from '../components/campaignSummary/ComplianceTab';
import AdherenceTab   from '../components/campaignSummary/AdherenceTab';
import RecruitmentTab from '../components/campaignSummary/RecruitmentTab';
import AttritionTab   from '../components/campaignSummary/AttritionTab';
import CensusTab      from '../components/campaignSummary/CensusTab';
import UpsellsTab     from '../components/campaignSummary/UpsellsTab';
import AIAgentInsightsTab    from '../components/campaignSummary/AIAgentInsightsTab';
import AIBusinessInsightsTab from '../components/campaignSummary/AIBusinessInsightsTab';
import EngagementsTab       from '../components/campaignSummary/EngagementsTab';
import BadgesTab from '../components/campaignSummary/BadgesTab';
import ScheduleVariancesTab from '../components/campaignSummary/ScheduleVariancesTab';
import CNEStatsTab          from '../components/campaignSummary/CNEStatsTab';
import TodoTab              from '../components/campaignSummary/TodoTab';
import SkillGroupTag        from '../components/campaignSummary/SkillGroupTag';
import AbsenceNotificationsPage from './AbsenceNotificationsPage';
import { usePageLabel } from '../components/BackChip';

/**
 * Everything about one campaign, the way the agent panel is everything about
 * one agent.
 *
 * Built as a hub rather than a second source of truth. Where a page already
 * computes something for a campaign -- call stats, the agent breakdown -- this
 * renders that page's own component scoped to the campaign, so a fix lands in
 * both places and the two can never drift into disagreeing.
 *
 * One file per tab, deliberately. The agent panel is a single 5,000-line
 * component and that size is why the same bug keeps recurring there: a column
 * selected in SQL, read by the UI, and quietly dropped by the code in between.
 *
 * The campaign and the date range live here and are passed down, so every tab
 * answers about the same campaign over the same window. A tab that filtered
 * itself would silently disagree with the one beside it.
 */
export default function CampaignSummaryPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);

  // Each tab renders a page that has its own route guard, and a guard protects
  // a route rather than a component -- embedding one bypasses it. So the
  // guards are mirrored here, tab by tab, against the same flags.
  //
  // Hiding, not blocking. The endpoints behind several of these check less
  // than their routes do, so this stops a tab being offered rather than
  // stopping the data being fetched. Tightening the endpoints is the other
  // half and is not done here.
  const lvl = (k) => currentUser?.[k] ?? 0;
  // A client's own login (Role 'client'): the same flags, less To Do and
  // Engagements (below).
  const isClient = currentUser?.Role === 'client';
  const CAN = {
    campaign:    lvl('CanViewCampaignAnalytics')  >= 1,
    evals:       lvl('CanEditEvals')              >= 1,
    errors:      lvl('CanEditErrors')             >= 1,
    coaching:    lvl('CanEditCoaching')           >= 1,
    compliance:  lvl('CanEditIncidents')          >= 1,
    adherence:   lvl('CanViewOversightStats')     >= 1,
    recruitment: lvl('CanViewRecruitmentStats')   >= 1,
    variances:   lvl('CanEditScheduleVariance')   >= 1,
    // The Absences page's own rule.
    absences:    currentUser?.Role !== 'agent' && lvl('CanEditAbsences') >= 1,
  };

  const [campaigns, setCampaigns] = useState([]);
  const [teams, setTeams]         = useState([]);
  const [teamId, setTeamId]       = useState(null);
  // Skill Groups (Admin > Business Config): named sets of a campaign's
  // skills. Offered only when the campaign has any; narrows the call-based
  // figures, and every other card and tab says it is not relevant.
  const [skillGroups, setSkillGroups] = useState([]);
  const [skillGroupId, setSkillGroupId] = useState(null);
  const [tab, setTab] = useState('overview');

  // The 1st of last month through today. Short on purpose: every tab taking
  // this window queries CallLog or AgentMonthlyStats across it, so the span is
  // most of what the page costs. Widen it when you need to and press Run.
  //
  // The Overview cards ignore it -- they work to fixed periods of their own,
  // month to date against last month -- which is why each tab that does use it
  // prints it rather than the page claiming one window for everything.
  const [range, setRange] = useState([
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs(),
  ]);

  // The dropdowns are a pending selection; nothing is fetched until Run.
  // Eleven tabs over two databases is far too much to re-run on every
  // keystroke of a dropdown, and changing campaign used to fire the lot.
  const [applied, setApplied] = useState(null);   // { campaignId, teamId, skillGroupId, from, to }

  // Its own endpoint rather than the shared options one, because this page
  // needs CXCampaignNo as well as the app's Campaigns.ID: the embedded call
  // pages key on the CX number and everything in the app database keys on the
  // ID. Restricted to the campaigns this user may see.
  useEffect(() => {
    api.get('/api/campaign-summary/campaigns')
      .then((r) => setCampaigns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCampaigns([]));
  }, []);

  // Teams and Campaigns are separate tables with nothing joining them, so a
  // team cannot be derived from a campaign. Both are asked for, and each tab
  // is scoped by whichever its data is actually keyed on -- evaluations,
  // calls, errors and coaching by campaign; compliance and attendance by team.
  //
  // my_teams=1 returns the teams this user may see, the same restriction the
  // Adherence page applies.
  useEffect(() => {
    api.get('/api/skill-groups')
      .then((r) => setSkillGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSkillGroups([]));
  }, []);

  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1 } })
      .then((r) => setTeams(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTeams([]));
  }, []);

  const selected = campaignId ? Number(campaignId) : null;
  const campaign = useMemo(
    () => campaigns.find((c) => c.ID === selected) || null,
    [campaigns, selected]
  );

  // The campaign is in the path, not in state, so a tab is linkable and the
  // back button moves between campaigns rather than out of the page.
  const chooseCampaign = (id) => navigate(id ? `/campaign-summary/${id}` : '/campaign-summary');

  // Someone who can see only one campaign, or only one team, gets it picked
  // for them. Replace, not push, so Back does not land on the empty page and
  // bounce straight back.
  useEffect(() => {
    if (!campaignId && campaigns.length === 1) {
      navigate(`/campaign-summary/${campaigns[0].ID}`, { replace: true });
    }
  }, [campaigns, campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (teamId == null && teams.length === 1) setTeamId(teams[0].ID);
  }, [teams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picking a campaign picks its default team (Admin > Campaigns) when the
  // user can see that team; they can still change it.
  const [campDefaults, setCampDefaults] = useState([]);
  useEffect(() => {
    api.get('/api/campaign-defaults').then((r) => setCampDefaults(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!campaignId) return;
    const d = campDefaults.find((x) => x.ID === Number(campaignId));
    if (d?.DefaultTeamID && teams.some((t) => t.ID === d.DefaultTeamID)) setTeamId(d.DefaultTeamID);
  }, [campaignId, campDefaults, teams]); // eslint-disable-line react-hooks/exhaustive-deps

  // A group belongs to one campaign: a new campaign clears the pick.
  const campaignGroups = useMemo(
    () => skillGroups.filter((sg) => sg.CampaignID === selected),
    [skillGroups, selected]
  );
  useEffect(() => { setSkillGroupId(null); }, [selected]);

  const team = useMemo(() => teams.find((t) => t.ID === teamId) || null, [teams, teamId]);
  // Tabs read what was applied, never the pending selection. Otherwise the
  // heading would name one campaign while the tables showed another.
  const appliedCampaign = useMemo(
    () => campaigns.find((c) => c.ID === applied?.campaignId) || null,
    [campaigns, applied]
  );
  const appliedTeam = useMemo(
    () => teams.find((t) => t.ID === applied?.teamId) || null,
    [teams, applied]
  );
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(appliedCampaign ? `Campaign Summary: ${appliedCampaign.Name}${appliedTeam ? ` / ${appliedTeam.Name}` : ''}` : null);
  const appliedGroup = useMemo(
    () => skillGroups.find((sg) => sg.ID === applied?.skillGroupId) || null,
    [skillGroups, applied]
  );
  const pendingFrom = range?.[0]?.format('YYYY-MM-DD') ?? null;
  const pendingTo   = range?.[1]?.format('YYYY-MM-DD') ?? null;

  const scope = {
    campaignId: applied?.campaignId ?? null,
    campaign:   appliedCampaign,
    teamId:     applied?.teamId ?? null,
    team:       appliedTeam,
    from:       applied?.from ?? null,
    to:         applied?.to ?? null,
    // The group's skill names, for the tabs that can be narrowed by them.
    skillGroup: appliedGroup,
    skills:     appliedGroup?.Skills?.length ? appliedGroup.Skills : null,
  };

  // Absent until the campaign is known, which is the same moment every other
  // tab becomes meaningful, so an unknown campaign keeps them rather than
  // flashing them away.
  const hasCalls = !appliedCampaign || !!appliedCampaign.HasCallData;

  const canRun  = !!selected && !!teamId && !!pendingFrom && !!pendingTo;
  const isStale = applied
    && (applied.campaignId !== selected
        || applied.teamId !== teamId
        || (applied.skillGroupId ?? null) !== skillGroupId
        || applied.from !== pendingFrom
        || applied.to !== pendingTo);

  // The AI Insights tabs on a campaign without AI Data.
  const aiEnabled = !!appliedCampaign?.AIData;
  const aiOff = (
    <Empty description="This campaign is not enabled to use this feature"
      image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
  );

  const tabs = [
    { key: 'overview',    label: 'Overview',     show: true,          children: <OverviewTab    {...scope} can={CAN} /> },
    // The agent To Do cards for the campaign / team; each card needs its area's access.
    // Not for a client: its rows open internal pages.
    { key: 'todo',        label: 'To Do',        show: !isClient && (CAN.evals || CAN.errors || CAN.coaching || CAN.compliance || CAN.variances),
      children: <TodoTab {...scope} /> },
    // Its own month picker rather than the page's range -- see the tab.
    // The Leaderboard's Goals view, so the Leaderboard's rule: not agents or
    // support agents (their Leaderboard has no Goals). A client's own login
    // gets it here though they have no Leaderboard page.
    { key: 'goals',       label: 'Agent Goals',  show: !['agent', 'support_agent'].includes(currentUser?.Role),
      children: <AgentGoalsTab  {...scope} /> },
    // Both read Superior's CallLog, so they are offered only where the
    // campaign has calls in it. Asked of CallLog rather than inferred from the
    // campaign being on CNE: those are different statements that coincide
    // today, and a campaign with both would lose tabs it should have.
    //
    // hasCalls is true while the campaign list is still loading, so the tabs do
    // not appear a moment after the rest.
    { key: 'calls',       bySkill: true, label: 'Call Stats', show: CAN.campaign && hasCalls,
      children: <CallStatsTab   {...scope} /> },
    { key: 'agents',      bySkill: true, label: 'Agent Call Stats', show: CAN.campaign && hasCalls,
      children: <AgentsTab {...scope} /> },
    // Shown where the campaign has CNE daily rows. Derived from the data by
    // the campaigns endpoint rather than declared on the campaign: both tables
    // are in this database, so there is nothing to keep in step and nothing to
    // remember to tick when a new campaign starts being uploaded.
    { key: 'cne',         label: 'CNE Stats',
      show: CAN.campaign && !!appliedCampaign?.HasCNEData,
      children: <CNEStatsTab {...scope} /> },
    // Offered only where the team is set up to see upselling at all. The same
    // HideUpsell flag already governs it on the agent panel, so a team that
    // does not upsell does not get a tab of empty months here either. Read off
    // the applied team rather than the pending one, so the tab list matches
    // what is actually on screen.
    { key: 'upsells',     label: 'Upsells',
      show: CAN.campaign && !!appliedTeam && !appliedTeam.HideUpsell,
      children: <UpsellsTab {...scope} /> },
    // Always offered, so people know the feature exists. They read tables that
    // only hold rows for AI-scored campaigns (Admin > Campaigns > AI Data), so
    // on any other campaign the tab says so instead of showing empty tables
    // (which would read as "nothing happened"). Not tied to the team's upsell
    // flag: a campaign can be AI-scored without upselling, and the reverse.
    { key: 'ai_agent',    bySkill: aiEnabled, label: 'AI Agent Insights',
      show: CAN.campaign,
      children: aiEnabled ? <AIAgentInsightsTab {...scope} /> : aiOff },
    { key: 'ai_business', bySkill: aiEnabled, label: 'AI Business Insights',
      show: CAN.campaign,
      children: aiEnabled ? <AIBusinessInsightsTab {...scope} /> : aiOff },
    // Evaluations sits beside Errors: both are quality findings against the
    // campaign, and they are read together.
    { key: 'evaluations', bySkill: true, label: 'Evaluations', show: CAN.evals,     children: <EvaluationsTab {...scope} /> },
    { key: 'errors',      label: 'Errors', show: CAN.errors,    children: <ErrorsTab      {...scope} /> },
    { key: 'coaching',    label: 'Coaching', show: CAN.coaching,  children: <CoachingTab    {...scope} /> },
    { key: 'compliance',  label: 'Compliance',     show: CAN.compliance, children: <ComplianceTab {...scope} /> },
    // The Absence Notifications page, locked to the selected team.
    { key: 'absences',    label: 'Absences',      show: CAN.absences && !!scope.teamId,
      children: <AbsenceNotificationsPage embedded teamId={scope.teamId} /> },
    { key: 'adherence',   label: 'Adherence',     show: CAN.adherence, children: <AdherenceTab   {...scope} /> },
    { key: 'recruitment', label: 'Recruitment',     show: CAN.recruitment, children: <RecruitmentTab {...scope} /> },
    { key: 'attrition',   label: 'Attrition',     show: CAN.recruitment, children: <AttritionTab {...scope} /> },
    { key: 'census',      label: 'Census',     show: CAN.recruitment, children: <CensusTab    {...scope} /> },
    // Ungated: reading which contests a team is in is not an edit right, and
    // anyone who can open this page can already see the team's numbers.
    // A client only with Engagements access.
    { key: 'engagements', label: 'Engagements', show: !isClient || lvl('CanEditEngagements') >= 1,
      children: <EngagementsTab {...scope} /> },
    // Ungated like Engagements; adding a badge needs CanAssignBadge.
    { key: 'badges',      label: 'Badges', show: true,
      children: <BadgesTab {...scope} /> },
    { key: 'variances',   label: 'Schedule Variances', show: CAN.variances,
      children: <ScheduleVariancesTab {...scope} /> },
  ].filter((t) => t.show)
    // With a Skill Group applied, each tab says whether it follows it. The
    // Overview marks its cards one by one instead (only Raw SLA follows it).
    .map(({ bySkill, ...t }) => (appliedGroup && t.key !== 'overview'
      ? { ...t, children: <><SkillGroupTag group={appliedGroup} relevant={!!bySkill} block />{t.children}</> }
      : t));

  // Falling back to the first visible tab, since the default key may name one
  // this user cannot see.
  const activeKey = tabs.some((t) => t.key === tab) ? tab : tabs[0]?.key;

  return (
    <>
      {/* Named for what is on screen, not for what is selected. */}
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Campaign Summary{appliedCampaign ? ` — ${appliedCampaign.Name}` : ''}
        {appliedTeam ? ` / ${appliedTeam.Name}` : ''}
      </Typography.Title>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Select a campaign"
          style={{ width: 260 }}
          value={selected}
          onChange={chooseCampaign}
          options={campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Select a team"
          style={{ width: 240 }}
          value={teamId}
          onChange={(v) => setTeamId(v ?? null)}
          options={teams.map((t) => ({ label: t.Name, value: t.ID }))}
        />
        {campaignGroups.length > 0 && (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Skill Group"
            style={{ width: 220 }}
            value={skillGroupId}
            onChange={(v) => setSkillGroupId(v ?? null)}
            options={campaignGroups.map((sg) => ({ label: sg.Name, value: sg.ID }))}
          />
        )}
        {/* Hidden where it changes nothing. Overview's cards work to fixed
            periods of their own, and Agent Goals is a month's standing with a
            month picker of its own. */}
        {activeKey !== 'overview' && activeKey !== 'goals' && (
          <RangePicker
            value={range}
            onChange={(v) => v && setRange(v)}
            allowClear={false}
            style={{ width: 250 }}
          />
        )}
        <Button
          type="primary"
          disabled={!canRun}
          onClick={() => setApplied({
            campaignId: selected, teamId, skillGroupId, from: pendingFrom, to: pendingTo,
          })}
        >
          Run
        </Button>
        {isStale && (
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            Selection changed — press Run
          </Typography.Text>
        )}
      </Space>

      {/* Nothing is fetched until Run: eleven tabs over two databases is far
          too much to re-run on a dropdown change. */}
      {!applied ? (
        <Empty
          description={canRun
            ? 'Press Run'
            : 'Select a campaign, a team and a date range, then press Run'}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginTop: 60 }}
        />
      ) : (
        <Tabs activeKey={activeKey} onChange={setTab} items={tabs} destroyInactiveTabPane={false} />
      )}
    </>
  );
}
